import FormQuestion from "./model/FormQuestion";
import {useState} from "react";

/**
 * Props for this component
 */
interface SingleSelectRadioQuestionProps {
  question: FormQuestion;
  locked: boolean;
  responseMap?: Map<number, string>;
  ariaControlsId?: string;
  onRadioSelect?: (
      questionId: number,
      selectedAnswer: string
  ) => void;
}

/**
 * This component represent a yes or no question. It can also be used to control other components
 *
 * @param question A {@link FormQuestion} object with the question details
 * @param locked A boolean to indicate if this component is locked or not
 * @param responseMap <b>Optional</b> A map to store the response this question produces
 * @param onRadioSelect <b>Optional</b> A function to run when a radio input is selected
 * @param ariaControlsId <b>Optional</b> The ID of a component that will be controlled by this component
 */
export default function YesOrNoRadioQuestion({
                                               question,
                                               locked,
                                               responseMap,
                                               ariaControlsId,
                                               onRadioSelect,
                                             }: SingleSelectRadioQuestionProps) {
  const [ariaComponentShowing, setAriaComponentShowing] = useState(ariaControlsId && question.response === "TRUE");

  /**
   * This function will add the response for this question to a map linking the response to its respective question
   * ID. In the case of a new response, it will save the value regardless, but if we are editing an existing response
   * we will only have the value in the map if it is different from the initial response.
   *
   * @param newResponse The value we wish to save
   */
  function updateResponseMap(newResponse: string) {
    if (!responseMap) {
      return; // This component is not required to save responses
    }

    if (question.response && responseMap.has(question.id)) {
      // This is to only add the answer change to the map when we edit an existing answer
      responseMap.delete(question.id);
    } else {
      // In case it is a new form answer we add it regardless
      responseMap.set(question.id, newResponse)
    }
  }

  return <div id={`singleSelectRadioQuestion${question.index}`} className="form-radio-area">
    {question.text ? (
            <label id={`singleSelectRadioQuestion${question.index}Label`} className="question-text">{question.text}</label>)
        :
        (<></>)}
    {locked ? (
        <span id={`singleSelectRadioQuestion${question.index}Response`}
              className="boolean-answer">{question.response === "TRUE" ? 'YES' : 'NO'}</span>
    ) : (
        <div id={`singleSelectRadioQuestion${question.index}RadioDiv`} className="yes-or-no-radio">
          <label id={`singleSelectRadioQuestion${question.index}YesText`} className="form-check-label"
                 htmlFor={`singleSelectRadioQuestion${question.index}YesInput`}>
            Yes
          </label>
          <input id={`singleSelectRadioQuestion${question.index}YesInput`} className="form-check-input" type="radio"
                 name={`singleSelectRadioQuestionSelection${question.index}`}
                 data-bs-toggle={!ariaComponentShowing ? "collapse" : ""}
                 data-bs-target={`#${ariaControlsId ? ariaControlsId : "No_Control"}`}
                 aria-expanded={"false"}
                 aria-controls={`${ariaControlsId ? ariaControlsId : "No_Control"}`}
                 required
                 onChange={() => {
                   // Trigger custom function if one was passed first
                   if (onRadioSelect) {
                     onRadioSelect(question.id, "TRUE");
                   }
                   updateResponseMap("TRUE");
                   setTimeout(() => {
                     setAriaComponentShowing(true);
                   }, 300);
                 }}
                 disabled={locked}
                 defaultChecked={question.response !== null && question.response === "TRUE"}
          />
          <label id={`singleSelectRadioQuestion${question.index}NoText`} className="form-check-label"
                 htmlFor={`singleSelectRadioQuestion${question.index}NoInput`}>
            No
          </label>
          <input id={`singleSelectRadioQuestion${question.index}NoInput`} className="form-check-input" type="radio"
                 name={`singleSelectRadioQuestionSelection${question.index}`}
                 data-bs-toggle={ariaComponentShowing ? "collapse" : ""}
                 data-bs-target={`#${ariaControlsId ? ariaControlsId : "No_Control"}`}
                 aria-expanded={"false"}
                 aria-controls={`${ariaControlsId ? ariaControlsId : "No_Control"}`}
                 required
                 onChange={() => {
                   // Trigger custom function if one was passed first
                   if (onRadioSelect) {
                       onRadioSelect(question.id, "FALSE");
                   }
                   updateResponseMap("FALSE")
                   setTimeout(() => {
                     setAriaComponentShowing(false);
                   }, 300);
                 }}
                 disabled={locked}
                 defaultChecked={question.response !== null && question.response === "FALSE"}
          />
          <div id="singleSelectRadioQuestionAreaFeedback" className="invalid-feedback">
            Please select one option.
          </div>
        </div>
    )}
  </div>
}
;